<template>
  <div class="AtlasGeoControls">
    <div
      v-if="allowGeoCompare"
      class="AtlasGeoControls-ctrl AtlasGeoControls-ctrl--geoMode"
    >
      <div class="AtlasGeoControls-label">Geography</div>
      <div class="AtlasGeoControls-geoToggle">
        <span
          v-for="mode in geoModeOptions"
          class="AtlasGeoControls-geoToggleOption"
          :class="{ 'is-active': mode.isActive }"
          :key="mode.id"
          @click.prevent="onSetGeoMode(mode, $event)"
        >
          {{ mode.text }}
          <atlas-icon
            v-if="mode.id === 'secondaryGeo' && secondaryGeo.id"
            class="AtlasGeoControls-geoCompareClose"
            name="close"
            :size="12"
            @click.prevent="cancelGeoCompare"
          />
        </span>
      </div>
    </div>

    <div
      class="AtlasGeoControls-ctrl AtlasGeoControls-ctrl--geoType"
      id="tour-selectGeoType"
    >
      <div class="AtlasGeoControls-label">Choose your geography type</div>
      <atlas-select :options="geoTypeOptions" @choose="onChooseGeoType" />
    </div>

    <div
      class="AtlasGeoControls-ctrl AtlasGeoControls-ctrl--geo"
      id="tour-selectGeo"
    >
      <div class="AtlasGeoControls-label">Choose your geography</div>
      <atlas-select :options="geoOptions" @choose="onChooseGeo" />
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import AtlasSelect from './select.vue';

const GeoMode = {
  PRIMARY: {
    id: 'primaryGeo',
    text: 'Select',
  },
  SECONDARY: {
    id: 'secondaryGeo',
    text: 'Compare',
  },
};

export default defineComponent({
  name: 'atlas-geo-controls',

  emits: ['update:primaryGeo', 'update:secondaryGeo', 'update:geoType'],

  components: {
    AtlasSelect,
  },

  props: {
    primaryGeo: {
      type: [Object, null],
      default: null,
    },
    secondaryGeo: {
      type: [Object, null],
      default: null,
    },

    geoSections: {
      type: Array,
      default: () => [],
    },

    allowGeoCompare: Boolean,

    isGeoCompareDefaultEnabled: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    geoTypeSelected: '',
    geoTypeCompareSelected: '',

    geoModeOptions: [
      { ...GeoMode.PRIMARY, isActive: true, query: 'geo' },
      { ...GeoMode.SECONDARY, isActive: false, query: 'geo_compare' },
    ],
  }),

  computed: {
    geoMode() {
      return this.geoModeOptions.find((mode) => mode.isActive) || {};
    },

    // There are type/geo dropdowns for both geo select and geo compare.
    // As each have their own state with different items, they are
    // kept as seperate arrays.
    // The computed properties "geoTypeOptions" and "geoOptions" point to the
    // active arrays for the current mode.
    geoTypeSelOptions() {
      return this.geoSections.map((geoSection) => ({
        ...geoSection,
        isActive: geoSection.text === this.geoTypeSelected,
      }));
    },
    geoTypeCmpOptions() {
      return this.geoSections.map((geoSection) => ({
        ...geoSection,
        isActive: geoSection.text === this.geoTypeCompareSelected,
      }));
    },

    geoTypeOptionsKey() {
      return {
        primaryGeo: 'geoTypeSelOptions',
        secondaryGeo: 'geoTypeCmpOptions',
      }[this.geoMode.id];
    },

    geoTypeOptions: {
      get() {
        return this[this.geoTypeOptionsKey];
      },
      set(options) {
        this[this.geoTypeOptionsKey] = options;
      },
    },
    geoType() {
      return this.geoTypeOptions.find((type) => type.isActive) || {};
    },

    geoOptions() {
      const getTypeActive = this.geoTypeOptions.find((op) => op.isActive);

      return getTypeActive?.children?.length
        ? getTypeActive.children.map((geo) =>
            this.makeGeoOption(
              geo,
              (this[this.geoMode.id] || {}).id,
              this.geoMode.query,
            ),
          )
        : [];
    },
  },

  watch: {
    allowGeoCompare: {
      immediate: true,
      handler(value) {
        // If geo-compare is switched off, force a revert to "Select" mode
        if (!value) {
          this.geoModeOptions = this.chooseOption(
            this.geoModeOptions,
            this.geoModeOptions[0],
          );
        }
      },
    },

    isGeoCompareDefaultEnabled: {
      immediate: true,
      handler(value) {
        // If breakdown is configured with a geo comparison by default
        // force Compare mode.
        if (value) {
          this.onSetGeoMode(GeoMode.SECONDARY, undefined);
        }
      },
    },

    primaryGeo() {
      this.setGeoType();
    },

    secondaryGeo() {
      this.setGeoType();
    },
  },

  methods: {
    makeGeoOption(geo, activeGeoCode) {
      return {
        text: geo.text,
        id: geo.id,
        isActive: geo.id === activeGeoCode,
      };
    },

    chooseOption(options, option) {
      return options.map((opt) => ({
        ...opt,
        isActive: option && option.text === opt.text,
      }));
    },

    onChooseGeo({ option }) {
      if (this.geoMode.id === GeoMode.PRIMARY.id) {
        this.$emit('update:primaryGeo', option);
      } else {
        this.$emit('update:secondaryGeo', option);
      }
    },

    updateGeoTypeModel({ option }) {
      if (this.geoMode.id === GeoMode.PRIMARY.id) {
        this.geoTypeSelected = option.text;
      } else {
        this.geoTypeCompareSelected = option.text;
      }
    },

    onChooseGeoType({ option }) {
      this.updateGeoTypeModel({ option });

      this.$emit('update:geoType', option);
    },

    async setGeoType() {
      // 1. Get the geo code for the active geoMode
      const geoTypeId = (this[this.geoMode.id] || {}).section;

      // 2. Find the active option in the geoType list
      const option = this.geoTypeOptions.find((opt) => opt.id === geoTypeId);

      if (option) {
        // 3. Apply updates to model
        this.updateGeoTypeModel({ option });
      }
    },

    onSetGeoMode(mode, event) {
      if (event?.target.tagName === 'svg') return; // clicked compare exit button
      // Select the desired mode (select vs. compare)
      this.geoModeOptions = this.chooseOption(this.geoModeOptions, mode);
      // Select the type
      this.setGeoType();
    },

    cancelGeoCompare() {
      this.$emit('update:secondaryGeo', { id: '' });
    },
  },

  mounted() {
    this.setGeoType();
  },
});
</script>

<style lang="scss">
.AtlasGeoControls {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  @media screen and (min-width: 760px) {
    display: flex;
  }

  &-ctrl {
    flex: 1;
    margin-right: 30px;
    margin-bottom: 15px;

    &:last-child {
      margin-bottom: 0;
    }

    @media screen and (min-width: 760px) {
      margin-bottom: 0;
    }
  }

  &-label {
    margin-bottom: 11px;
    color: color(shadow);
    font-size: 14px;
    font-weight: normal;
    white-space: nowrap;
  }

  &-geoToggle {
    white-space: nowrap;
  }

  &-geoToggleOption {
    display: inline-block;
    padding: 0 15px;
    margin-right: 3px;
    background-color: color(bg);
    color: color(shadow);
    cursor: pointer;
    font-size: 15px;
    font-weight: bold;
    line-height: 32px;
    text-transform: uppercase;

    &.is-active {
      background-color: color(active-bg);
      color: color(black);
      cursor: default;
    }
  }

  &-geoCompareClose {
    display: inline-block;
    margin: -2px 0 0 15px;
    cursor: pointer;
  }
}
</style>
